// Planning, Completion and Single audit (DESIGN §3, DATA_CONTRACT H, J, K): the engine's document read models laid out for
// the CPA — values verbatim with their sources, FIRM POLICY labelled, each gap with the command that closes it — plus the
// stage steps they belong to and the queue's actions in that area. Where the engine does not publish a model the screen needs,
// it says so ("engine support needed") and still offers the recording commands. A model the engine could not compute shows as
// a failure, never as "nothing to do". Human decisions (materiality, risk, variants, going concern, conclusions,
// reassessment, majors, opinions) are forms only a person can send.
import type { ReactNode } from "react";
import { useApp } from "../../app/context";
import { AgentChip, Card, DataTable, SourceChip, StatusPill } from "../../components";
import { source } from "../../data/rows";
import type { Action, DocModel } from "../../data/types";
import { fmtStamp, titleCase } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { useEngagement } from "./Layout";
import { Act, DocCard, Docs, Facts, GapList, NotPublished, QueueList, RecordTable, SmartDoc, asList, asStrs, useAct, useDocs, val, type Open } from "./shared";

/** The stage steps a screen belongs to, with the engine's state and reason. */
export function StageSteps({ stageIds, open }: { stageIds: string[]; open?: Open }) {
  const { model } = useEngagement();
  const steps = model.stages.filter((s) => stageIds.includes(s.stageId)).flatMap((s) => s.steps);
  return (
    <Card title={`Steps · ${stageIds.join(", ")}`} bodyClass="p-3">
      <DataTable rows={steps} rowKey={(s) => s.stepId} dense caption="Stage steps" empty="No steps published."
        filter={{ id: "state", label: "state", value: (s) => s.state, labelFor: titleCase }}
        columns={[
          { id: "id", header: "Step", cell: (s) => <span className="font-mono text-xs">{s.stepId}</span> },
          { id: "name", header: "What", cell: (s) => s.name },
          { id: "state", header: "State", cell: (s) => <StatusPill state={s.state} /> },
          { id: "why", header: "Why", cell: (s) => <span className="block text-xs text-muted">{s.reason ?? ""}{open && s.gaps.filter((g) => g.command).map((g, i) => <span key={i} className="mt-1 flex flex-wrap items-center gap-1"><span>{g.text}</span><Act command={g.command!} title={g.text} open={open} label={`${g.command}…`} /></span>)}</span> },
        ]} />
    </Card>
  );
}

const byCommand = (actions: Action[], pred: (c: string) => boolean) => actions.filter((a) => pred(a.command));
const policy = (s: unknown) => (s ? <span className="rounded border border-waiting-line bg-waiting-bg px-1 text-[11px] text-waiting" title="Firm policy: overridable with a recorded rationale">{String(s)}</span> : null);
const cite = (s: unknown) => (typeof s === "string" && s ? <SourceChip source={source(s)} /> : <span className="text-[11px] text-blocked">no source</span>);

// ─────────────────────────────── Planning ───────────────────────────────

export function PlanningPage() {
  const { id, model } = useEngagement();
  const { ds } = useApp();
  const { open, drawer } = useAct();
  const docs = useDocs("planning");
  const findings = useLoad(() => ds.table(id, "findings"), [ds, id]);
  const linkage = model.openItems.filter((i) => i.source === "linkage.check" && i.status === "open");
  return (
    <div className="space-y-4">
      <Docs state={docs}>
        {(d) => (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              <DocCard docs={d} k="materiality" title="Materiality" action={<Act command="materiality.set" title="Set materiality (partner's decision)" open={open} label="Set…" />}>
                {(doc) => <Materiality doc={doc} />}
              </DocCard>
              <DocCard docs={d} k="prelim" title="Preliminary analytics" action={<Act command="prelim.run" title="Run the preliminary analysis" open={open} label="Run…" />}>
                {(doc) => (
                  <div className="space-y-2">
                    <Facts items={[["Run", val(doc.run)], ["When", fmtStamp(doc.at as string)], ["By", val(doc.by)], ["Why", val(doc.reason)]]} />
                    {asList(doc.findings).length > 0 && <RecordTable title="Findings" rows={asList(doc.findings)} columns={["n", "area", "test", "severity", "text"]} />}
                    {asStrs(doc.files).length > 0 && <p className="text-xs text-muted">Files: {asStrs(doc.files).map((f) => f.split("/").pop()).join(" · ")}</p>}
                  </div>
                )}
              </DocCard>
            </div>
            {!d.find((x) => x.kind === "prelim") && (
              <Card title="Preliminary findings (latest run)" bodyClass="p-3">
                <DataTable rows={findings.data} loading={findings.loading} error={findings.error} rowKey={(f) => `${f.run}:${f.n}`} dense caption="Preliminary findings" empty="No preliminary run published."
                  columns={[{ id: "n", header: "#", cell: (f) => f.n }, { id: "area", header: "Area", cell: (f) => f.area }, { id: "sev", header: "Severity", cell: (f) => <StatusPill state={f.severity} /> }, { id: "text", header: "Finding", cell: (f) => f.text }]} />
              </Card>
            )}
            <DocCard docs={d} k="tb_lead" title="Books and trial balance (lead schedules)" action={<span className="flex flex-wrap gap-1"><Act command="tb.import" title="Import a trial balance" open={open} label="Import TB…" /><Act command="mapping.confirm" title="Confirm the account mapping" open={open} label="Confirm mapping…" /></span>}>
              {(doc) => <TbLead doc={doc} />}
            </DocCard>
            <DocCard docs={d} k="risks" title="Risk assessment" action={<Act command="risk.assess" title="Assess a risk (a person's judgment)" open={open} label="Assess…" />}>
              {(doc) => asList(doc.risks).length ? <RecordTable rows={asList(doc.risks)} columns={["area", "assertion", "inherent", "control", "rmm", "significant", "fraud", "rationale", "by"]} /> : <SmartDoc doc={doc} open={open} />}
            </DocCard>
            <DocCard docs={d} k="variants" title="Program variants">
              {(doc) => <Variants doc={doc} open={open} />}
            </DocCard>
            <div className="grid gap-4 lg:grid-cols-2">
              <DocCard docs={d} k="controls" title="Controls and walkthroughs" action={<Act command="walkthrough.record" title="Record a walkthrough" open={open} label="Walkthrough…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="inquiries" title="Inquiries and fraud discussion" action={<Act command="inquiry.record" title="Record an inquiry" open={open} label="Inquiry…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="estimates" title="Estimates" action={<Act command="estimate.register" title="Register an estimate" open={open} label="Register…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="group" title="Group audit" action={<Act command="group.record" title="Record the group determination" open={open} label="Record…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
            </div>
          </>
        )}
      </Docs>
      <Card title="Linkage (risks → procedures)" action={<Act command="linkage.check" title="Check risk-to-procedure linkage" open={open} label="Check linkage…" />}>
        {linkage.length ? <ul className="list-disc space-y-1 pl-4 text-sm">{linkage.map((i) => <li key={i.key}>{i.text}</li>)}</ul> : <p className="text-sm text-muted">No open linkage items from the engine's last check.</p>}
      </Card>
      <StageSteps stageIds={["S10", "S11", "S13"]} open={open} />
      {drawer}
    </div>
  );
}

function Materiality({ doc }: { doc: Record<string, unknown> }) {
  const c = (doc.current && typeof doc.current === "object" ? doc.current : doc) as Record<string, unknown>;
  const hist = asList(doc.history);
  return (
    <div className="space-y-3">
      <Facts items={[
        ["Benchmark", <span key="b">{val(c.benchmark)} · {val(c.benchmark_amount)} {cite(c.benchmark_source)}</span>],
        ["Planning", <span key="p" className="font-mono">{val(c.planning)}</span>],
        ["Performance", <span key="pf" className="font-mono">{val(c.performance)} <span className="text-muted">(factor {val(c.performance_factor)})</span></span>],
        ["Clearly trivial", <span key="t" className="font-mono">{val(c.trivial)} <span className="text-muted">(factor {val(c.trivial_factor)})</span></span>],
        ["Rationale", <span key="r">{val(c.rationale)} <SourceChip source={{ kind: "auditor-judgment", ref: `judgment:${String(c.by ?? "")}`, resolved: !!c.by }} /></span>],
        ["Policy", policy(c.policy) ?? val(null)],
        ["Set by", <span key="by" className="inline-flex flex-wrap items-center gap-1">{val(c.by)} · {fmtStamp(c.at as string)}{c.agent ? <AgentChip name={`typed by ${String(c.agent)}`} /> : null}</span>],
      ]} />
      <div>
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">History</h3>
        {hist.length ? <RecordTable rows={hist} columns={["id", "planning", "performance", "trivial", "by", "at", "superseded_by"]} /> : <p className="text-sm text-muted">No earlier materiality published.</p>}
        <p className="mt-1 text-xs text-muted">A change marks every consumer workpaper stale (hub); they must be rendered again.</p>
      </div>
    </div>
  );
}

function TbLead({ doc }: { doc: Record<string, unknown> }) {
  const areas = doc.areas && typeof doc.areas === "object" ? Object.entries(doc.areas as Record<string, Record<string, unknown>>) : [];
  if (!areas.length) return <p className="text-sm text-muted">No lead schedules published.</p>;
  return (
    <div className="space-y-3">
      {areas.map(([k, a]) => (
        <details key={k} className="rounded border border-line">
          <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-2 py-1.5 text-sm">
            <span className="font-medium">{titleCase(k)}</span><span className="font-mono text-xs text-muted">{val(a.program)}</span><span className="text-xs text-muted">{val(a.fs_line)}</span>
            <span className="ml-auto font-mono text-xs">CY {val(a.total_cy)} · adjusted {val(a.total_adjusted)}</span>
          </summary>
          <div className="p-2"><RecordTable rows={asList(a.accounts)} columns={["code", "name", "py", "cy", "adjustments", "adjusted", "confirmed"]} /></div>
        </details>
      ))}
      <p className="text-xs text-muted">Amounts exactly as the engine published them (tb.lead).</p>
    </div>
  );
}

function Variants({ doc, open }: { doc: Record<string, unknown>; open: Open }) {
  const areas = asList(doc.areas);
  if (!areas.length) return <SmartDoc doc={doc} open={open} />;
  return (
    <div className="space-y-3">
      {doc.policy ? <p className="text-xs">{policy("FIRM POLICY")} <span className="text-muted">{String(doc.policy)}</span></p> : null}
      <ul className="divide-y divide-line">
        {areas.map((a) => (
          <li key={String(a.area)} className="py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{titleCase(String(a.area))}</span>
              {a.chosen || a.variant ? <StatusPill state="done" label={`Chosen: ${String(a.chosen ?? a.variant)}`} /> : <StatusPill state="waiting" label="No variant recorded" />}
              {a.computed_default ? <span className="text-xs text-muted">engine default: {String(a.computed_default)}</span> : null}
              <span className="ml-auto"><Act command="program.variant" title={`Choose the ${String(a.area)} program variant (manager's decision)`} open={open} fixed={{ area: String(a.area) }} label="Choose…" /></span>
            </div>
            <ul className="mt-1 space-y-0.5 text-xs">
              {asList(a.options).map((o) => (
                <li key={String(o.variant)}><span className="font-mono">{String(o.template)}</span> {String(o.variant)} — {o.eligible ? "eligible" : `not eligible${asStrs(o.failed).length ? `: ${asStrs(o.failed).join("; ")}` : ""}`}
                  {asStrs(o.conditions).length > 0 && <span className="block text-muted">needs: {asStrs(o.conditions).join("; ")}</span>}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────── Completion ───────────────────────────────

const GC = (c: string) => c.startsWith("gc.");
const RP = (c: string) => c.startsWith("rp.") || c === "absence.record";

export function CompletionPage() {
  const { model } = useEngagement();
  const { open, drawer } = useAct();
  const docs = useDocs("completion");
  const planning = useDocs("planning");
  const q = model.actions;
  return (
    <div className="space-y-4">
      <Docs state={docs}>
        {(d) => (
          <>
            <DocCard docs={d} k="gaps" title="What completion still needs (completion.gaps)">{(doc) => <GapList gaps={asStrs(doc.gaps)} open={open} title="Gaps" />}</DocCard>
            <div className="grid gap-4 lg:grid-cols-2">
              <Section title="Going concern" actions={byCommand(q, GC)} open={open} extra={<><Act command="gc.indicators" title="Compute the going-concern indicators" open={open} label="Indicators…" /><Act command="gc.evaluate" title="Evaluate going concern (partner's decision)" open={open} label="Evaluate…" /></>}>
                <NotPublished what="the going-concern indicators and evaluation" command="gc.indicators / gc.evaluate" />
              </Section>
              <Section title="Related parties" actions={byCommand(q, RP)} open={open} extra={<><Act command="rp.add" title="Record a related party" open={open} label="Add…" /><Act command="absence.record" title="Record that there are none (with sources)" open={open} label="None exist…" /></>}>
                <NotPublished what="the related-party register" command="rp.list" />
              </Section>
              <DocCard docs={d} k="conclusions" title="Conclusions" action={<Act command="conclusion.record" title="Record a conclusion (partner's decision)" open={open} label="Record…" />}>
                {(doc) => asList(doc.conclusions).length ? <RecordTable rows={asList(doc.conclusions)} columns={["topic", "conclusion", "detail", "by", "at"]} /> : <p className="text-sm text-muted">No conclusion recorded yet.</p>}
              </DocCard>
              <DocCard docs={d} k="misstatements" title="Misstatements">{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="deficiencies" title="Control deficiencies" action={<Act command="deficiency.evaluate" title="Evaluate a deficiency's severity (partner's decision)" open={open} label="Evaluate…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="legal" title="Legal letters" action={<span className="flex flex-wrap gap-1"><Act command="legal.counsel" title="Identify legal counsel" open={open} label="Counsel…" /><Act command="legal.response" title="Record a legal response" open={open} label="Response…" /></span>}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <DocCard docs={planning.data} k="inquiries" title="Inquiries (management and governance)" action={<Act command="inquiry.record" title="Record an inquiry" open={open} label="Inquiry…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <Section title="Final analytics" actions={byCommand(q, (c) => c.startsWith("final."))} open={open} extra={<><Act command="final.analytics" title="Run the final analytical review" open={open} label="Run…" /><Act command="final.explain" title="Explain a fluctuation" open={open} label="Explain…" /></>}>
                <NotPublished what="the final analytical review's lines" command="final.analytics" />
              </Section>
              <Section title="Reassessment of materiality and risk" actions={byCommand(q, (c) => c === "completion.reassess")} open={open} extra={<Act command="completion.reassess" title="Reassess materiality and risk (partner's decision)" open={open} label="Reassess…" />} />
              <DocCard docs={d} k="mgmt_letter" title="Management letter" action={<span className="flex flex-wrap gap-1"><Act command="mgmtletter.draft" title="Draft the management letter" open={open} label="Draft…" /><Act command="mgmtletter.recommend" title="Add a recommendation" open={open} label="Recommend…" /><Act command="mgmtletter.finalize" title="Finalize the management letter (partner)" open={open} label="Finalize…" /></span>}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="confirmations" title="Confirmations">{(doc) => asList(doc.confirmations).length ? <RecordTable rows={asList(doc.confirmations)} columns={["id", "area", "party", "kind", "amount", "reply_amount", "status", "sent_by"]} /> : <SmartDoc doc={doc} open={open} />}</DocCard>
              <DocCard docs={d} k="judgments" title="Significant judgments" action={<Act command="judgment.record" title="Record a judgment" open={open} label="Record…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
            </div>
          </>
        )}
      </Docs>
      <StageSteps stageIds={["S16", "S17"]} open={open} />
      {drawer}
    </div>
  );
}

function Section({ title, actions, open, extra, children }: { title: string; actions: Action[]; open: Open; extra?: ReactNode; children?: ReactNode }) {
  return (
    <Card title={title} action={extra ? <span className="flex flex-wrap gap-1">{extra}</span> : undefined}>
      <div className="space-y-2">{children}<QueueList actions={actions} open={open} empty="Nothing about this in the engine's queue." /></div>
    </Card>
  );
}

// ─────────────────────────────── Single audit ───────────────────────────────

const SA = (c: string) => /^(sefa|majors|compliance|finding|findings|icoc|sa|fac)\./.test(c) || c === "program.materiality";

export function SingleAuditPage() {
  const { model } = useEngagement();
  const { open, drawer } = useAct();
  const docs = useDocs("completion");
  const h = model.header;
  const sa = (d: DocModel[]) => d.filter((x) => x.kind === "single_audit_checklists" || x.kind === "fac");
  return (
    <div className="space-y-4">
      <Card>
        <p className="text-sm">{h.singleAudit ? "The engine records a single audit (Uniform Guidance) for this engagement." : "The engine has not flagged a single audit for this engagement: its single-audit records exist only when the Uniform Guidance applies."}
          {h.gagas ? " Yellow Book (GAGAS) applies." : ""}</p>
      </Card>
      <Docs state={docs}>
        {(d) => sa(d).length === 0 && !h.singleAudit ? null : (
          <div className="grid gap-4 lg:grid-cols-2">
            <DocCard docs={d} k="single_audit_checklists" title="Single audit checklists">{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
            <DocCard docs={d} k="fac" title="Data collection form (FAC)" action={<Act command="fac.record" title="Record a FAC event" open={open} label="Record…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
          </div>
        )}
      </Docs>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="SEFA" action={<span className="flex flex-wrap gap-1"><Act command="sefa.import" title="Import the SEFA" open={open} label="Import…" /><Act command="sefa.reconcile" title="Reconcile the SEFA" open={open} label="Reconcile…" /></span>}>
          <NotPublished what="the schedule of expenditures of federal awards" command="sefa.compare" />
        </Card>
        <Card title="Major programs" action={<Act command="majors.determine" title="Determine the major programs (manager's decision)" open={open} label="Determine…" />}>
          <NotPublished what="the major-program determination" command="majors.determine result" />
        </Card>
        <Card title="Compliance requirements and tests" action={<span className="flex flex-wrap gap-1"><Act command="compliance.set" title="Set the compliance requirements" open={open} label="Requirements…" /><Act command="compliance.sample" title="Plan a compliance sample" open={open} label="Sample…" /><Act command="compliance.results" title="Record compliance results" open={open} label="Results…" /></span>}>
          <NotPublished what="compliance requirements A–N, tests and results" />
        </Card>
        <Card title="Findings" action={<span className="flex flex-wrap gap-1"><Act command="finding.add" title="Add a finding" open={open} label="Add…" /><Act command="finding.respond" title="Record the auditee's response" open={open} label="Response…" /></span>}>
          <NotPublished what="the schedule of findings and questioned costs" command="findings.schedule" />
        </Card>
        <Card title="Reports" action={<span className="flex flex-wrap gap-1"><Act command="compliance.opinion" title="Form the compliance opinion (partner)" open={open} label="Opinion…" /><Act command="program.materiality" title="Set program materiality (partner)" open={open} label="Program materiality…" /></span>}>
          <NotPublished what="the single audit reports" />
        </Card>
        <Card title="In the engine's queue"><QueueList actions={byCommand(model.actions, SA)} open={open} empty="No single-audit work queued." /></Card>
      </div>
      <StageSteps stageIds={["S12", "S15"]} open={open} />
      {drawer}
    </div>
  );
}
